import {
    Avatar,
    Box,
    Button,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    Textarea,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createChat, getMyProfile, uploadFilesForChat } from "../../../api";
import { IMyProfile } from "../../../types";

const MAX_CONTENT_LENGTH = 2000;
const MAX_IMAGE_ATTACHMENTS = 10;

interface AddModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPostStart?: () => void;
    /** API 응답 완료 시 호출. 피드 갱신은 Feed 쪽에서 처리 */
    onPostDone?: () => void;
    onPostError?: () => void;
}

interface ImageAttachment {
    file: File;
    previewUrl: string;
}

interface SortableImageAttachmentCardProps {
    dndId: string;
    previewUrl: string;
    index: number;
    onDelete: () => void;
    isPending: boolean;
}

function SortableImageAttachmentCard({
    dndId,
    previewUrl,
    index,
    onDelete,
    isPending,
}: SortableImageAttachmentCardProps) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: dndId,
        disabled: isPending,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            position="relative"
            width={36}
            height={36}
            borderRadius={12}
            overflow="hidden"
            cursor={isPending ? "not-allowed" : "grab"}
        >
            <Image
                src={previewUrl}
                alt={`첨부 이미지 ${index + 1}`}
                width="100%"
                height="100%"
                objectFit="cover"
                draggable={false}
            />
            <Box
                as="button"
                type="button"
                position="absolute"
                top={1}
                right={1}
                onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
                onClick={onDelete}
            >
                <MyIcon
                    src="/svg/delete-circle.svg"
                    alt="이미지 삭제"
                    width={24}
                    height={24}
                />
            </Box>
        </Box>
    );
}

export default function AddModal({ onPostStart, onPostDone, onPostError, ...props }: AddModalProps) {

    /* query */

    const queryClient = useQueryClient();
    const toast = useToast();

    const { data: myProfileData } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: getMyProfile,
    });
    
    /* content ~ */

    const [content, setContent] = useState("");
    const [imageAttachments, setImageAttachments] = useState<ImageAttachment[]>([]);

    const [isMyTypeOnly, setIsMyTypeOnly] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const canPost = content.trim().length > 0;
    const canAddMoreImages = imageAttachments.length < MAX_IMAGE_ATTACHMENTS;

    /* useEffect: 모달 종료 시 초기화 */

    useEffect(() => {
        if (!props.isOpen) {
            setContent("");
            setIsMyTypeOnly(false);
            setImageAttachments((prev) => {
                prev.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
                return [];
            });
        }
    }, [props.isOpen]);


    /* createChatMutation */

    const createChatMutation = useMutation({
        mutationFn: async (payload: {
            content: string;
            files: File[];
            isMyTypeOnly: boolean;
            profileId: string;
        }) => {
            const imageUrls = payload.files.length > 0
                ? await uploadFilesForChat({ profileId: payload.profileId, files: payload.files })
                : [];

            return createChat({
                content: payload.content,
                imageUrls,
                isMyTypeOnly: payload.isMyTypeOnly,
            });
        },
        onSuccess: () => {
            // 피드 갱신은 인디케이터가 꺼질 때 Feed에서 처리하므로 여기서 invalidate 하지 않음
            props.onClose();
            onPostDone?.();
        },
        onError: (error) => {
            console.error(error);
            onPostError?.();
            toast({
                title: "게시에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* handleFileChange: 이미지 업로드 */

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";

        if (files.length === 0) return;

        const imageFiles = files.filter((file) => file.type.startsWith("image/"));
        if (imageFiles.length !== files.length) {
            toast({
                title: "이미지 파일만 업로드할 수 있습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (imageAttachments.length + imageFiles.length > MAX_IMAGE_ATTACHMENTS) {
            toast({
                title: `이미지는 최대 ${MAX_IMAGE_ATTACHMENTS}장까지 첨부할 수 있습니다.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        setImageAttachments((prev) => [
            ...prev,
            ...imageFiles.map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
            })),
        ]);
    };

    /* removeImage: 이미지 삭제 */

    const removeImage = (index: number) => {
        setImageAttachments((prev) => {
            URL.revokeObjectURL(prev[index].previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    /* drag and drop handlers */

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const dndIds = useMemo(
        () => imageAttachments.map(({ previewUrl }, index) => `${index}::${previewUrl}`),
        [imageAttachments]
    );

    const onDragEnd = (event: DragEndEvent) => {
        if (createChatMutation.isPending) return;

        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = dndIds.findIndex((id) => id === String(active.id));
        const newIndex = dndIds.findIndex((id) => id === String(over.id));

        if (oldIndex < 0 || newIndex < 0) return;

        setImageAttachments((prev) => arrayMove(prev, oldIndex, newIndex));
    };

    /* handlePost: 게시 */

    const handlePost = () => {
        if (!canPost || createChatMutation.isPending) return;

        const profileId = myProfileData?.id;
        if (!profileId) return;

        const payload = {
            content: content.trim(),
            files: imageAttachments.map(({ file }) => file),
            isMyTypeOnly,
            profileId,
        };

        onPostStart?.();
        createChatMutation.mutate(payload);
    };



    /* render */

    return (
        <Modal
            motionPreset="slideInBottom"
            size="2xl"
            isOpen={props.isOpen}
            onClose={props.onClose}
        >
            <ModalOverlay />
            <ModalContent borderRadius={16} overflow="hidden">
                <ModalHeader
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    borderBottom="0.2px solid"
                    borderColor={colors.g4}
                    padding={3}
                >
                    <Text
                        as="button"
                        type="button"
                        fontSize={16}
                        fontWeight={500}
                        color={colors.black}
                        cursor="pointer"
                        onClick={props.onClose}
                    >
                        취소
                    </Text>
                </ModalHeader>

                <ModalBody padding={5}>
                    <HStack alignItems="flex-start" gap={3}>
                        {myProfileData?.avatar ? (
                            <Image
                                src={myProfileData?.avatar}
                                alt={`${myProfileData?.nickname} 프로필 이미지`}
                                width={10}
                                height={10}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        ) : (
                            <Image
                                src="/png/profile.png"
                                alt="프로필 이미지"
                                width={10}
                                height={10}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        )}

                        <Stack flex={1} gap={3} alignItems="flex-start">
                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                {myProfileData?.nickname}
                            </Text>

                            {/* 콘텐츠 입력 영역 */}

                            <Textarea
                                value={content}
                                onChange={(event) => setContent(event.target.value)}
                                placeholder={"어떤 소식이 있나요?"}
                                maxLength={MAX_CONTENT_LENGTH}
                                variant="unstyled"
                                fontSize={16}
                                fontWeight={400}
                                color={colors.g1}
                                minHeight={120}
                                resize="none"
                                padding={0}
                                _placeholder={{ color: colors.g3 }}
                            />

                            {/* 이미지 미리보기 영역 */}

                            {imageAttachments.length > 0 && (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={onDragEnd}
                                >
                                    <SortableContext items={dndIds} strategy={rectSortingStrategy}>
                                        <HStack gap={2} flexWrap="wrap">
                                            {imageAttachments.map(({ previewUrl }, index) => (
                                                <SortableImageAttachmentCard
                                                    key={dndIds[index]}
                                                    dndId={dndIds[index]}
                                                    previewUrl={previewUrl}
                                                    index={index}
                                                    onDelete={() => removeImage(index)}
                                                    isPending={createChatMutation.isPending}
                                                />
                                            ))}
                                        </HStack>
                                    </SortableContext>
                                </DndContext>
                            )}

                            {/* 이미지 업로드 input */}

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                            />

                            {/* 이미지 업로드 버튼 */}

                            <Box
                                as="button"
                                type="button"
                                onClick={() => {
                                    if (!canAddMoreImages) return;
                                    fileInputRef.current?.click();
                                }}
                                opacity={canAddMoreImages ? 1 : 0.4}
                                cursor={canAddMoreImages ? "pointer" : "not-allowed"}
                            >
                                <MyIcon
                                    src="/svg/photo.svg"
                                    alt="이미지 업로드"
                                    width={24}
                                    height={24}
                                />
                            </Box>
                        </Stack>
                    </HStack>

                    {/* 안내 문구 */}

                    <VStack
                        width="100%"
                        alignItems="flex-end"
                        gap={2}
                        marginTop={5}
                    >
                        <Text fontSize={12} fontWeight={400} color={colors.g3}>24시간 동안 게시되며, 이후 프로필에 보관됩니다.</Text>
                    </VStack>
                </ModalBody>

                <ModalFooter
                    borderTop="0.2px solid"
                    borderColor={colors.g4}
                    padding={3}
                >
                    <HStack width="100%" justifyContent="space-between" alignItems="center">
                        <HStack
                            as="button"
                            type="button"
                            gap={2}
                            alignItems="center"
                            cursor="pointer"
                            onClick={() => setIsMyTypeOnly((prev) => !prev)}
                        >
                            <MyIcon
                                src={isMyTypeOnly ? "/svg/check-blue.svg" : "/svg/check.svg"}
                                alt="내 유형에게만"
                                width={20}
                                height={20}
                            />
                            <Text 
                                fontSize={14} 
                                fontWeight={isMyTypeOnly ? 700 : 500} 
                                color={isMyTypeOnly ? colors.blue : colors.g2}
                            >
                                내 유형에게만
                            </Text>
                        </HStack>
                        <Button
                            size="sm"
                            borderRadius="full"
                            paddingX={6}
                            variant="ghost"
                            color={canPost ? colors.blue : colors.g3}
                            _hover={{
                                backgroundColor: canPost ? colors.light : colors.g5,
                            }}
                            _active={{
                                backgroundColor: canPost ? colors.light : colors.g5,
                            }}
                            isDisabled={!canPost || !myProfileData?.id}
                            isLoading={createChatMutation.isPending}
                            onClick={handlePost}
                        >
                            게시
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
